import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { InlineEdit } from '@shared/ui/InlineEdit';
import { AnalyzingBadge } from '@shared/ui/AnalyzingBadge';
import { IconCheck, IconFile, IconRefresh } from '@shared/ui/icons';
import {
  apiErrorText,
  usePatchContractMutation,
  useUpdateContractNoteMutation,
  useSetContractNumberMutation,
  useSetContractProjectMutation,
  useAnalyzeContractMutation,
  useForceAnalyzeContractMutation,
  useListStatutProjectsQuery,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import { analysisStarted } from '@features/analysisProgress/model/slice';
import { CONTRACT_TYPE_OPTIONS, contractStatusLabel, contractTypeLabel } from '@shared/lib/statusLabels';
import { InjectionWarning, type InjectionFlag } from '../InjectionWarning';
import { SigningVerdictBanner } from '../SigningVerdictBanner';
import { scoreContract } from '../contractScoring';
import {
  ConcludedBadge,
  dt,
  DuplicateList,
  DuplicateWarning,
  Empty,
  Grid,
  NotesArea,
  NumberPlaceholder,
  PrecontractBadge,
  Section,
} from './styles';

const CardButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 2px 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const AutoLabel = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary}1f;
  color: ${({ theme }) => theme.colors.primary};
  cursor: help;
`;

const FeedbackNotice = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)};
  background: ${({ theme }) => theme.colors.status.info}0f;
  border-left: 4px solid ${({ theme }) => theme.colors.status.info};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.5;
`;

const FeedbackTitle = styled.strong`
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.status.info};
`;

const FeedbackList = styled.ul`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  padding-left: 20px;
`;

const ManualReviewBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)};
  background: ${({ theme }) => theme.colors.status.warning}14;
  border-left: 4px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.5;
`;

const BannerActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2.5)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

/** Разбор комплекта не удался: без этого баннера юрист видел только
 *  «Не удалось обработать» — ни причины, ни что делать. */
const ParseProblemBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)};
  background: ${({ theme }) => theme.colors.status.danger}12;
  border-left: 4px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.5;
`;

const BannerTitle = styled.strong`
  display: block;
  font-size: 14px;
`;

const BannerText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const BannerHint = styled.span`
  align-self: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SkippedFiles = styled.ul`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  padding-left: 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SkippedReason = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Файл комплекта, который разбор пропустил (`metadata.parse_skipped`). */
interface SkippedFile {
  file: string;
  reason: string;
  /** `false` — doc-processor отверг сам файл (формат, повреждение): повтор
   *  дал бы тот же отказ. В записях до 03.11.2026 поля нет — считаем, что
   *  повтор может помочь. */
  retry?: boolean;
}

const SKIPPED_SHOWN = 5;

function SkippedFilesList({ files }: { files: SkippedFile[] }) {
  const rest = files.length - SKIPPED_SHOWN;
  return (
    <SkippedFiles>
      {files.slice(0, SKIPPED_SHOWN).map((f) => (
        <li key={f.file}>
          <strong>{f.file}</strong>
          <SkippedReason> — {f.reason}</SkippedReason>
        </li>
      ))}
      {rest > 0 && <li>и ещё {rest}</li>}
    </SkippedFiles>
  );
}

const ActionRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const HeaderCell = styled.dd`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

import type { ContractDetails } from '@entities/legal/types';

interface ContractHeaderSectionProps {
  contractId: string;
  data: ContractDetails;
  can_write: boolean;
  onOpenCounterparty: (id: string) => void;
  onOpenChat: () => void;
  onDownloadConclusion: () => void;
  onConclude: () => void;
  isDownloading: boolean;
  isConcluding: boolean;
  concludeError: string | null;
}

export function ContractHeaderSection({
  contractId,
  data,
  can_write,
  onOpenCounterparty,
  onOpenChat,
  onDownloadConclusion,
  onConclude,
  isDownloading,
  isConcluding,
  concludeError,
}: ContractHeaderSectionProps) {
  const [patchContract, { isLoading: isPatching }] = usePatchContractMutation();
  const [updateNote, { isLoading: isSavingNote }] = useUpdateContractNoteMutation();
  const [setContractNumber, { isLoading: isSavingNumber }] = useSetContractNumberMutation();
  const [setContractProject, { isLoading: isSettingProject }] = useSetContractProjectMutation();
  const [forceAnalyze, { isLoading: isForceAnalyzing }] = useForceAnalyzeContractMutation();
  const [reparse, { isLoading: isReparsing }] = useAnalyzeContractMutation();
  const [reparseError, setReparseError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { data: projectsData } = useListStatutProjectsQuery();

  const parseMeta = data.contract.metadata as {
    parse_error?: string | null;
    analysis_error?: string | null;
    parse_skipped?: SkippedFile[] | null;
    parse_aborted_reason?: string | null;
    aborted_reason?: string | null;
  } | null;
  const failed = data.contract.status === 'parse_failed';
  // Файлы разобраны, но анализ не дошёл до конца (ИИ не ответил). Сервер
  // пишет одну из двух причин: analysis_error или parse_error.
  const analysisError = failed ? (parseMeta?.analysis_error ?? null) : null;
  const parseError =
    failed && !analysisError ? (parseMeta?.parse_error ?? 'Не удалось разобрать файлы комплекта.') : null;
  const skippedRaw = parseMeta?.parse_skipped;
  const skippedFiles: SkippedFile[] = Array.isArray(skippedRaw) ? skippedRaw : [];
  // Повтор поможет хотя бы одному файлу. Если все отвергнуты по формату,
  // кнопка только заново прогнала бы анализ всего комплекта с тем же итогом.
  const skippedRetryable = skippedFiles.some((f) => f.retry !== false);
  const inProgress = data.contract.status === 'parsing' || data.contract.status === 'under_review';

  /** «Повторить разбор» = «Перезапустить анализ»: сервер сам доразбирает
   *  файлы без текста, уже разобранные не трогает, затем запускает анализ. */
  async function onReparse() {
    setReparseError(null);
    dispatch(analysisStarted({ contract_id: contractId }));
    try {
      await reparse(contractId).unwrap();
    } catch (err) {
      setReparseError(apiErrorText(err, 'Не удалось запустить повторный разбор.'));
    }
  }

  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [numberDuplicates, setNumberDuplicates] = useState<
    Array<{ id: string; contract_number: string | null; subject: string | null; signed_at: string | null }>
  >([]);
  const [confirmForceAnalyze, setConfirmForceAnalyze] = useState(false);

  async function onSaveNumber(raw: string) {
    const trimmed = raw.trim();
    const res = await setContractNumber({
      id: contractId,
      contractNumber: trimmed === '' ? null : trimmed,
    }).unwrap();
    setNumberDuplicates(res.duplicate_warning?.duplicates ?? []);
  }

  const flags = (data.contract.metadata as { injection_flags?: InjectionFlag[] })?.injection_flags;
  const feedbackRound = (
    data.contract.metadata as { feedback_round?: { granted?: { filename: string; reason?: string | null }[] } }
  )?.feedback_round;
  const granted = feedbackRound?.granted;

  return (
    <>
      {data.contract.record_kind === 'precontract' &&
        (data.contract.concluded_contract_id ? (
          <ConcludedBadge role="status">
            <IconCheck size={14} />
            Заключён
            <Link
              to={`/agents/statut/contracts/${data.contract.concluded_contract_id}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              перейти в «Договоры»
            </Link>
          </ConcludedBadge>
        ) : (
          <PrecontractBadge role="status">Преддоговорный</PrecontractBadge>
        ))}

      <Grid>
        <dt>Контрагент</dt>
        <HeaderCell>
          <InlineEdit
            value={data.contract.counterparty}
            busy={isPatching}
            onSave={async (raw) => {
              await patchContract({ id: contractId, counterparty: raw }).unwrap();
            }}
          />
          {data.contract.counterparty_id && (
            <CardButton
              type="button"
              onClick={() => onOpenCounterparty(data.contract.counterparty_id!)}
              title="Открыть карточку контрагента"
            >
              📇 Карточка
            </CardButton>
          )}
        </HeaderCell>

        <dt>Номер договора</dt>
        <dd>
          <InlineEdit
            value={data.contract.contract_number ?? ''}
            display={
              data.contract.contract_number ? (
                data.contract.contract_number
              ) : (
                <NumberPlaceholder>Номер не определён</NumberPlaceholder>
              )
            }
            placeholder="указать вручную, напр. 2024/СМР-17"
            busy={isSavingNumber}
            onSave={onSaveNumber}
          />
        </dd>

        <dt>Предмет</dt>
        <dd>
          <InlineEdit
            value={data.contract.subject ?? ''}
            display={data.contract.subject ?? '—'}
            placeholder="напр., устройство монолитных конструкций"
            busy={isPatching}
            onSave={async (raw) => {
              await patchContract({ id: contractId, subject: raw }).unwrap();
            }}
          />
        </dd>

        <dt>Сумма</dt>
        <dd>
          <InlineEdit
            kind="number"
            step="0.01"
            value={data.contract.sum?.toString() ?? ''}
            display={
              data.contract.sum != null
                ? `${data.contract.sum.toLocaleString('ru-RU')} ${data.contract.currency ?? ''}`
                : '—'
            }
            busy={isPatching}
            onSave={async (raw) => {
              const n = Number(raw);
              if (!Number.isFinite(n)) throw new Error('Не число');
              await patchContract({ id: contractId, sum: n }).unwrap();
            }}
          />
        </dd>

        <dt>Тип</dt>
        <dd>
          <InlineEdit
            kind="select"
            options={CONTRACT_TYPE_OPTIONS}
            value={data.contract.contract_type ?? 'works'}
            display={contractTypeLabel(data.contract.contract_type)}
            busy={isPatching}
            onSave={async (raw) => {
              await patchContract({ id: contractId, contract_type: raw }).unwrap();
            }}
          />
        </dd>

        <dt>Подписан</dt>
        <dd>
          <InlineEdit
            kind="date"
            value={data.contract.signed_at ? data.contract.signed_at.slice(0, 10) : ''}
            display={dt(data.contract.signed_at)}
            busy={isPatching}
            onSave={async (raw) => {
              if (!raw) return;
              const iso = new Date(`${raw}T00:00:00Z`).toISOString();
              await patchContract({ id: contractId, signed_at: iso }).unwrap();
            }}
          />
        </dd>

        <dt>Истекает</dt>
        <HeaderCell>
          <InlineEdit
            kind="date"
            value={data.contract.expires_at ? data.contract.expires_at.slice(0, 10) : ''}
            display={dt(data.contract.expires_at)}
            busy={isPatching}
            onSave={async (raw) => {
              if (!raw) return;
              const iso = new Date(`${raw}T00:00:00Z`).toISOString();
              await patchContract({ id: contractId, expires_at: iso }).unwrap();
            }}
          />
          {(() => {
            const src = (
              data.contract.metadata as {
                expires_at_source?: { kind?: string; amount?: number; unit?: string; matched_text?: string };
              }
            )?.expires_at_source;
            if (!src || src.kind !== 'relative_duration_parser') return null;
            const unitLabel: Record<string, string> = {
              day: src.amount === 1 ? 'день' : 'дн.',
              month: 'мес.',
              year: src.amount === 1 ? 'год' : 'г.',
            };
            const label = `${src.amount ?? '?'} ${unitLabel[src.unit ?? ''] ?? src.unit ?? ''} с подписания`;
            return <AutoLabel title={src.matched_text ?? 'Вычислено из текста договора'}>авто · {label}</AutoLabel>;
          })()}
        </HeaderCell>

        <dt>Объект</dt>
        <dd>
          <Select
            value={data.contract.project_id ?? ''}
            disabled={!can_write || isSettingProject}
            onChange={(e) => {
              const val = e.target.value;
              void setContractProject({ contractId, project_id: val ? val : null });
            }}
          >
            <option value="">— Не привязан к объекту —</option>
            {(projectsData?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ${p.name}` : p.name}
              </option>
            ))}
          </Select>
        </dd>

        <dt>Статус</dt>
        <dd>
          {data.contract.status === 'under_review' ? (
            <AnalyzingBadge />
          ) : data.contract.status === 'parsing' ? (
            <AnalyzingBadge label="Разбор файлов" />
          ) : (
            <Badge>{contractStatusLabel(data.contract.status)}</Badge>
          )}
        </dd>
      </Grid>

      <Section>
        <h4>Заметка</h4>
        <NotesArea
          value={noteDraft ?? data.contract.note ?? ''}
          onChange={(e) => setNoteDraft(e.target.value)}
          disabled={!can_write}
          placeholder="Свободная заметка по договору: где оригиналы, статус подписания, «расторгнут по соглашению сторон», «наш экземпляр передан в бухгалтерию на хранение», «подписан электронно» и т.п."
        />
        {noteDraft !== null && noteDraft !== (data.contract.note ?? '') && (
          <ActionRow>
            <Button
              type="button"
              $size="sm"
              onClick={async () => {
                await updateNote({ id: contractId, note: (noteDraft ?? '').trim() || null }).unwrap();
                setNoteDraft(null);
              }}
              disabled={isSavingNote || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isSavingNote ? 'Сохраняем…' : 'Сохранить заметку'}
            </Button>
            <Button type="button" $size="sm" $variant="secondary" onClick={() => setNoteDraft(null)}>
              Отменить
            </Button>
          </ActionRow>
        )}
      </Section>

      {numberDuplicates.length > 0 && (
        <DuplicateWarning role="status">
          <strong>
            У этого контрагента уже есть{' '}
            {numberDuplicates.length === 1 ? 'договор' : `${numberDuplicates.length} договор(а/ов)`} с таким номером.
          </strong>{' '}
          Это подсказка — номер всё равно сохранён, проверьте, не дубликат ли это.
          <DuplicateList>
            {numberDuplicates.map((d) => (
              <li key={d.id}>
                <span>№ {d.contract_number ?? '—'}</span>
                <span>·</span>
                <span style={{ flex: 1, minWidth: 0 }}>{d.subject ?? 'без предмета'}</span>
                <span>{dt(d.signed_at)}</span>
              </li>
            ))}
          </DuplicateList>
        </DuplicateWarning>
      )}

      {Array.isArray(flags) && flags.length > 0 && <InjectionWarning flags={flags} />}

      {Array.isArray(granted) && granted.length > 0 && (
        <FeedbackNotice>
          <FeedbackTitle>
            <IconRefresh size={16} aria-hidden />
            Анализ дополнен по запросу агента
          </FeedbackTitle>
          <div style={{ marginTop: 6, color: 'var(--osnova-text-secondary, #6b7280)' }}>
            Агент запросил документы из комплекта, которых не было в первичном анализе, и переанализировал договор с их
            учётом:
          </div>
          <FeedbackList>
            {granted.map((g) => (
              <li key={g.filename} style={{ marginBottom: 2 }}>
                <strong>{g.filename}</strong>
                {g.reason ? <span style={{ color: 'var(--osnova-text-muted, #9ca3af)' }}> — {g.reason}</span> : null}
              </li>
            ))}
          </FeedbackList>
        </FeedbackNotice>
      )}

      {parseError && (
        <ParseProblemBanner role="alert">
          <BannerTitle>Не удалось разобрать файлы комплекта</BannerTitle>
          <BannerText>{parseError}</BannerText>
          {skippedFiles.length > 0 && <SkippedFilesList files={skippedFiles} />}
          <BannerActions>
            <Button
              type="button"
              onClick={onReparse}
              disabled={!can_write || isReparsing || inProgress}
              title={!can_write ? 'Только чтение' : 'Разобрать файлы без текста и запустить анализ'}
            >
              {isReparsing ? 'Запускаем…' : 'Повторить разбор'}
            </Button>
            <BannerHint>Уже разобранные файлы повторно не обрабатываются.</BannerHint>
          </BannerActions>
          {reparseError && <BannerText role="alert">{reparseError}</BannerText>}
        </ParseProblemBanner>
      )}

      {analysisError && (
        <ParseProblemBanner role="alert">
          <BannerTitle>Анализ не завершён</BannerTitle>
          <BannerText>{analysisError}</BannerText>
          <BannerActions>
            <Button
              type="button"
              onClick={onReparse}
              disabled={!can_write || isReparsing || inProgress}
              title={!can_write ? 'Только чтение' : 'Запустить анализ комплекта заново'}
            >
              {isReparsing ? 'Запускаем…' : 'Перезапустить анализ'}
            </Button>
            <BannerHint>Разобранные файлы повторно не обрабатываются.</BannerHint>
          </BannerActions>
          {reparseError && <BannerText role="alert">{reparseError}</BannerText>}
        </ParseProblemBanner>
      )}

      {!parseError && skippedFiles.length > 0 && (
        <ManualReviewBanner role="status">
          <BannerTitle>Не разобрано файлов: {skippedFiles.length} — в анализ они не попали</BannerTitle>
          <SkippedFilesList files={skippedFiles} />
          <BannerActions>
            {skippedRetryable ? (
              <>
                <Button
                  type="button"
                  $variant="secondary"
                  onClick={onReparse}
                  disabled={!can_write || isReparsing || inProgress}
                  title={!can_write ? 'Только чтение' : 'Ещё раз разобрать только эти файлы и обновить анализ'}
                >
                  {isReparsing ? 'Запускаем…' : 'Повторить разбор'}
                </Button>
                <BannerHint>Остальные файлы разобраны — повтор займётся только этими.</BannerHint>
              </>
            ) : (
              <BannerHint>
                Повтор не поможет: файлы не открываются в этом формате. Откройте их, сохраните как .docx или
                .xlsx и догрузите в комплект.
              </BannerHint>
            )}
          </BannerActions>
          {reparseError && <BannerText role="alert">{reparseError}</BannerText>}
        </ManualReviewBanner>
      )}

      {data.contract.status === 'manual_review' && (
        <ManualReviewBanner>
          {!confirmForceAnalyze ? (
            <>
              <strong style={{ fontSize: 14 }}>⚠ Договор отправлен на ручную проверку</strong>
              <div style={{ marginTop: 6, color: 'var(--osnova-text-secondary, #6b7280)' }}>
                {parseMeta?.aborted_reason ??
                  parseMeta?.parse_aborted_reason ??
                  'Комплект слишком большой для автоматического анализа — на разбор такого объёма ушли бы часы. Проверьте состав комплекта или запустите анализ вручную.'}
              </div>
              <BannerActions>
                <Button
                  type="button"
                  onClick={() => setConfirmForceAnalyze(true)}
                  disabled={!can_write}
                  title={!can_write ? 'Только чтение' : undefined}
                >
                  ▶ Запустить анализ
                </Button>
                <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--osnova-text-muted, #9ca3af)' }}>
                  Альтернатива — разбить договор на разделы и загрузить каждый отдельно (быстрее и дешевле).
                </span>
              </BannerActions>
            </>
          ) : (
            <>
              <strong style={{ fontSize: 14 }}>Подтвердите запуск анализа</strong>
              <div style={{ marginTop: 6, color: 'var(--osnova-text-secondary, #6b7280)' }}>
                Анализ может занять <strong>от 30 минут до нескольких часов</strong> и стоить <strong>$1-5</strong>{' '}
                LLM-токенов в зависимости от размера договора. Юрист увидит прогресс в карточке. Если упрётся в дневной
                лимит бюджета ($15) — анализ автоматически приостановится до сброса 24-часового окна.
              </div>
              <BannerActions>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      await forceAnalyze(contractId).unwrap();
                      setConfirmForceAnalyze(false);
                    } catch {
                      // ошибка остаётся на confirm-state
                    }
                  }}
                  disabled={isForceAnalyzing || !can_write}
                  title={!can_write ? 'Только чтение' : undefined}
                >
                  {isForceAnalyzing ? 'Запускаем…' : 'Подтвердить и запустить'}
                </Button>
                <Button
                  type="button"
                  $variant="secondary"
                  onClick={() => setConfirmForceAnalyze(false)}
                  disabled={isForceAnalyzing}
                >
                  Отмена
                </Button>
              </BannerActions>
            </>
          )}
        </ManualReviewBanner>
      )}

      <SigningVerdictBanner verdict={scoreContract(data.contract.status, data.risks)} />

      <ActionRow>
        <Button
          type="button"
          onClick={onOpenChat}
          disabled={data.contract.status === 'under_review'}
          title={
            data.contract.status === 'under_review'
              ? 'Дождитесь окончания анализа'
              : 'Открыть чат с агентом по этому договору'
          }
        >
          💬 Спросить агента
        </Button>
        <Button
          type="button"
          $variant="secondary"
          onClick={onDownloadConclusion}
          disabled={isDownloading || data.contract.status === 'under_review'}
          title="DOCX-заключение для руководителя ЮС / директора"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <IconFile size={16} />
            {isDownloading ? 'Готовим…' : 'Заключение юр.отдела'}
          </span>
        </Button>
        {data.contract.record_kind === 'precontract' && !data.contract.concluded_contract_id && (
          <Button
            type="button"
            onClick={onConclude}
            disabled={isConcluding || !can_write}
            title={
              !can_write
                ? 'Только чтение'
                : 'Создать чистую карточку договора для подписанного скана в реестре «Договоры»'
            }
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconCheck size={16} />
              {isConcluding ? 'Отмечаем…' : 'Договор заключён'}
            </span>
          </Button>
        )}
      </ActionRow>
      {concludeError && (
        <Empty role="alert" $danger style={{ marginTop: 8 }}>
          {concludeError}
        </Empty>
      )}
    </>
  );
}
